import { Collection } from '@/types/collection';

// 인기 컬렉션 목록 (우선순위 높음)
const FEATURED_COLLECTIONS = [
  'wine',
  'red-wine',
  'white-wine',
  'champagne',
  'sparkling-wine',
  'whiskey',
  'vodka',
  'gin',
  'rum',
  'tequila',
  'cognac',
  'brandy',
  'barolo',
  'cabernet-sauvignon',
  'pomerol'
];

// Featured에서 제외할 컬렉션 목록
const EXCLUDED_FROM_FEATURED = [
  'discountable-red-wines',
  'discountable-rose', 
  'discountable-white-wine',
  'wine-items-for-discount'
];

// 카테고리별 우선순위
const CATEGORY_PRIORITY: Record<string, number> = {
  'wine': 1,
  'champagne': 2,
  'sparkling': 2,
  'whiskey': 3,
  'vodka': 4,
  'gin': 5,
  'rum': 6,
  'tequila': 7,
  'cognac': 8,
  'brandy': 9
};

export interface CollectionWithPriority extends Collection {
  priority: number;
  featured: boolean;
  category: string;
}

/**
 * 컬렉션의 카테고리를 추출
 */
export function getCollectionCategory(collection: Collection): string {
  const title = collection.title.toLowerCase();
  const description = collection.description?.toLowerCase() || '';
  
  if (title.includes('champagne') || title.includes('sparkling') || 
      description.includes('champagne') || description.includes('sparkling')) {
    return 'champagne';
  }
  
  if (title.includes('wine') || description.includes('wine') ||
      title.includes('red') || title.includes('white') || title.includes('rose')) {
    return 'wine';
  }
  
  if (title.includes('whiskey') || title.includes('whisky')) {
    return 'whiskey';
  }
  
  if (title.includes('vodka')) {
    return 'vodka';
  }
  
  if (title.includes('gin')) {
    return 'gin';
  }
  
  if (title.includes('rum')) {
    return 'rum';
  }
  
  if (title.includes('tequila')) {
    return 'tequila';
  }
  
  if (title.includes('cognac')) {
    return 'cognac';
  }
  
  if (title.includes('brandy')) {
    return 'brandy';
  }
  
  return 'other';
}

/**
 * 컬렉션이 인기 컬렉션인지 확인
 */
export function isFeaturedCollection(collection: Collection): boolean {
  const handle = collection.handle.toLowerCase();
  
  // 제외 목록에 있으면 Featured가 아님
  if (EXCLUDED_FROM_FEATURED.some(excluded => handle.includes(excluded))) {
    return false;
  }
  
  // Featured 목록에 있으면 Featured
  return FEATURED_COLLECTIONS.some(featured => handle.includes(featured));
}

/**
 * 컬렉션의 우선순위 계산
 */
export function getCollectionPriority(collection: Collection): number {
  const category = getCollectionCategory(collection);
  const featured = isFeaturedCollection(collection);
  
  // 인기 컬렉션은 높은 우선순위
  if (featured) {
    return CATEGORY_PRIORITY[category] || 10;
  }
  
  // 카테고리별 우선순위
  return CATEGORY_PRIORITY[category] || 100;
}

/**
 * 컬렉션을 우선순위에 따라 정렬
 */
export function sortCollectionsByPriority(collections: Collection[]): CollectionWithPriority[] {
  return collections
    .map(collection => ({
      ...collection,
      priority: getCollectionPriority(collection),
      featured: isFeaturedCollection(collection),
      category: getCollectionCategory(collection)
    }))
    .sort((a, b) => {
      // 인기 컬렉션을 먼저
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      
      // 우선순위가 높은 것 먼저
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      
      // 우선순위가 같으면 알파벳 순
      return a.title.localeCompare(b.title);
    });
}

/**
 * 컬렉션을 인기도 순으로 정렬
 */
export function sortCollectionsByPopularity(collections: Collection[]): CollectionWithPriority[] {
  return collections
    .map(collection => ({
      ...collection,
      priority: getCollectionPriority(collection),
      featured: isFeaturedCollection(collection),
      category: getCollectionCategory(collection)
    }))
    .sort((a, b) => {
      // 인기도 점수 계산 (간단한 예시)
      const aScore = getPopularityScore(a);
      const bScore = getPopularityScore(b);
      
      if (aScore !== bScore) {
        return bScore - aScore; // 높은 점수 먼저
      }
      
      // 점수가 같으면 알파벳 순
      return a.title.localeCompare(b.title);
    });
}

/**
 * 컬렉션의 인기도 점수 계산 (간단한 예시)
 */
function getPopularityScore(collection: CollectionWithPriority): number {
  let score = 0;
  
  // 인기 컬렉션 보너스
  if (collection.featured) score += 100;
  
  // 카테고리별 기본 점수
  score += (100 - collection.priority);
  
  // 제목 길이 (짧은 제목이 더 인기 있을 수 있음)
  score += Math.max(0, 20 - collection.title.length);
  
  return score;
} 

// lib/collections-utils.ts

// 컬렉션 URL 파라미터 타입 정의
export interface CollectionUrlParams {
  sort?: string;
  page?: number;
  country?: string;
  region?: string;
  sub_region?: string;
  wine_appellation?: string;
  wine_category?: string;
  varietals?: string;
  price_min?: number;
  price_max?: number;
  q?: string; // 검색 쿼리
  [key: string]: string | number | undefined; // 인덱스 시그니처 추가
}

// 정렬 옵션 정의
export const SORT_OPTIONS = {
  'best-match': { label: 'Best Match', value: 'products' },
  'price-asc': { label: 'Price: Low to High', value: 'products/sort/price:asc' },
  'price-desc': { label: 'Price: High to Low', value: 'products/sort/price:desc' },
  'name-asc': { label: 'Name: A to Z', value: 'products/sort/title:asc' },
  'name-desc': { label: 'Name: Z to A', value: 'products/sort/title:desc' },
  'newest': { label: 'Newest First', value: 'products/sort/created_at:desc' },
  'oldest': { label: 'Oldest First', value: 'products/sort/created_at:asc' },
  'updated': { label: 'Recently Updated', value: 'products/sort/updated_at:desc' }
} as const;

// URL 파라미터를 파싱하는 함수
export function parseCollectionUrlParams(searchParams: URLSearchParams): CollectionUrlParams {
  const params: CollectionUrlParams = {};

  // 정렬 파라미터
  const sort = searchParams.get('sort');
  if (sort && Object.keys(SORT_OPTIONS).includes(sort)) {
    params.sort = sort;
  }

  // 페이지 파라미터
  const page = searchParams.get('page');
  if (page) {
    const pageNum = parseInt(page, 10);
    if (pageNum > 0) {
      params.page = pageNum;
    }
  }

  // 필터 파라미터들
  const filters = ['country', 'region', 'sub_region', 'wine_appellation', 'wine_category', 'varietals'];
  filters.forEach(filter => {
    const value = searchParams.get(filter);
    if (value) {
      params[filter as keyof CollectionUrlParams] = value;
    }
  });

  // 가격 범위 파라미터
  const price = searchParams.get('price');
  if (price) {
    const [min, max] = price.split('-').map(p => parseInt(p, 10));
    if (!isNaN(min)) params.price_min = min;
    if (!isNaN(max)) params.price_max = max;
  }

  // 검색 쿼리
  const q = searchParams.get('q');
  if (q) {
    params.q = q;
  }

  return params;
}

// URL 파라미터를 생성하는 함수
export function buildCollectionUrlParams(params: CollectionUrlParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  // 정렬
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }

  // 페이지 (1페이지는 URL에 포함하지 않음)
  if (params.page && params.page > 1) {
    searchParams.set('page', params.page.toString());
  }

  // 필터들
  const filters = ['country', 'region', 'sub_region', 'wine_appellation', 'wine_category', 'varietals'];
  filters.forEach(filter => {
    const value = params[filter as keyof CollectionUrlParams];
    if (value) {
      searchParams.set(filter, value.toString());
    }
  });

  // 가격 범위
  if (params.price_min || params.price_max) {
    const min = params.price_min || 0;
    const max = params.price_max || 9999;
    searchParams.set('price', `${min}-${max}`);
  }

  // 검색 쿼리
  if (params.q) {
    searchParams.set('q', params.q);
  }

  return searchParams;
}

// 컬렉션 URL을 생성하는 함수
export function buildCollectionUrl(handle: string, params: CollectionUrlParams = {}): string {
  const searchParams = buildCollectionUrlParams(params);
  const queryString = searchParams.toString();
  
  return `/collections/${handle}${queryString ? `?${queryString}` : ''}`;
}

// 파라미터가 유효한지 검증하는 함수
export function validateCollectionParams(params: CollectionUrlParams): boolean {
  // 페이지 번호 검증
  if (params.page && (params.page < 1 || params.page > 1000)) {
    return false;
  }

  // 가격 범위 검증
  if (params.price_min && params.price_max && params.price_min > params.price_max) {
    return false;
  }

  // 정렬 옵션 검증
  if (params.sort && !Object.keys(SORT_OPTIONS).includes(params.sort)) {
    return false;
  }

  return true;
}

// 파라미터를 SEO 친화적인 메타데이터로 변환하는 함수
export function paramsToSeoData(params: CollectionUrlParams, collectionData: any) {
  const filters = [];
  
  if (params.country) filters.push(params.country);
  if (params.region) filters.push(params.region);
  if (params.wine_category) filters.push(params.wine_category);
  if (params.varietals) filters.push(params.varietals);
  
  const priceRange = params.price_min || params.price_max 
    ? `$${params.price_min || 0}${params.price_max ? ` - $${params.price_max}` : '+'}`
    : '';

  return {
    filters,
    priceRange,
    sortLabel: params.sort ? SORT_OPTIONS[params.sort as keyof typeof SORT_OPTIONS]?.label : null
  };
}

// URL 파라미터를 Typesense 필터 문자열로 변환하는 함수
export function paramsToTypesenseFilter(params: CollectionUrlParams, collectionHandle: string): string {
  const filters: string[] = [];
  
  // 기본 컬렉션 필터
  filters.push(`collection_handles:${collectionHandle}`);
  
  // 국가 필터
  if (params.country) {
    filters.push(`country:=${params.country}`);
  }
  
  // 지역 필터
  if (params.region) {
    filters.push(`region:=${params.region}`);
  }
  
  // 서브 지역 필터
  if (params.sub_region) {
    filters.push(`sub_region:=${params.sub_region}`);
  }
  
  // 와인 애펠레이션 필터
  if (params.wine_appellation) {
    filters.push(`wine_appellation:=${params.wine_appellation}`);
  }
  
  // 와인 카테고리 필터
  if (params.wine_category) {
    filters.push(`wine_category:=${params.wine_category}`);
  }
  
  // 품종 필터
  if (params.varietals) {
    filters.push(`varietals:=${params.varietals}`);
  }
  
  // 가격 범위 필터
  if (params.price_min || params.price_max) {
    const min = params.price_min || 0;
    const max = params.price_max || 9999;
    filters.push(`price:>=${min} && price:<=${max}`);
  }
  
  return filters.join(' && ');
} 